import React from "react";
import { useTranslation } from "react-i18next";
import { ArrowDownward, ArrowUpward, DeleteForever, DragIndicator, Edit } from "@mui/icons-material";
import { Box, Button, ButtonGroup, Checkbox, Divider, IconButton, Paper, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { useSortable } from '@dnd-kit/sortable';

import { ColumnDialogMode, ColumnDialogParams, ColumnModelItem } from "./ColumnModel";

interface ColumnItemProps {
  columnItem: ColumnModelItem;
  // Index of "this" column
  colIndex: number;
  // Length of visible columns array
  colListLength: number;
  // Unique column identifier
  field: string;
  // Dialog options when clicking on header name
  hasHeaderOptions: boolean | undefined;
  // Display label of this column
  headerName: string;
  id: string;
  // Column is activate when it's selected
  isColumnActive: boolean;
  moveColumnDown: () => void;
  moveColumnUp: () => void;
  onColumnDialogMode?: (params: ColumnDialogParams) => void;
  // Hides or deletes a column by index
  onRemoveColumn: (columnItem: ColumnModelItem) => void;
  toggleActiveColumn: () => void;
}

export default function ColumnItem(props: ColumnItemProps) {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const columnStyle = {
    background: props.isColumnActive
      ? theme.colors.primary.lighter
      : theme.palette.background.paper,
    position: "relative",
    touchAction: "none"
  };
  const isUpButtonDisabled = props.colIndex === 0;
  const isDownButtonDisabled = props.colIndex === props.colListLength - 1;

  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: props.id });
  
  const draggingStyle = {
    cursor: "pointer",
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : "unset",
    transition,
    ...(isDragging &&
      {
        background: theme.palette.primary.main,
        opacity: .6,
        zIndex: 10
      }
    )
  };

  return (
    <Paper ref={setNodeRef} sx={columnStyle} style={draggingStyle}>
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%"
        }}>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            gap: 1
          }}>
          {/* Make column active */}
          <Checkbox
            checked={props.isColumnActive}
            onClick={props.toggleActiveColumn}
          />
          <Divider orientation="vertical" flexItem />
          {/* Move up & down buttons */}
          <ButtonGroup
            size="small"
            sx={{ "&.MuiButtonGroup-root > button > span": { margin: 0 } }}>
            <Button
              disabled={isUpButtonDisabled}
              disableRipple
              onClick={() => !isUpButtonDisabled && props.moveColumnUp()}
              startIcon={<ArrowUpward />}
            />
            <Button
              disabled={isDownButtonDisabled}
              disableRipple
              onClick={() => !isDownButtonDisabled && props.moveColumnDown()}
              startIcon={<ArrowDownward />}
            />
          </ButtonGroup>
          <Divider orientation="vertical" flexItem />
        </Stack>
        {/* Draggable Area */}
        <Stack
          {...attributes}
          {...listeners}
          direction="row"
          sx={[{
            alignItems: "center",
            width: "100%"
          }, ...(Array.isArray(listeners.sx) ? listeners.sx : [listeners.sx])]}>
          <DragIndicator className="row-drag-icon" sx={{ opacity: .15 }} />
          <Box
            sx={{
              marginLeft: "auto",
              marginRight: "auto"
            }}
          >
            {/* Column Header Name - no header options */}
            {!props.hasHeaderOptions && <Typography variant="h5">{props.headerName}</Typography>}
            {/* Column Header Name - Editable text */}
            {props.hasHeaderOptions &&
              <Button
                endIcon={<Edit />}
                sx={{ color: theme.palette.text.primary }}
                onClick={() => {
                  if (props.onColumnDialogMode) {
                    props.onColumnDialogMode({
                      mode: ColumnDialogMode.UPDATE_COLUMN,
                      activeColumnIndex: props.colIndex,
                      activeField: props.field
                    });
                  }
                }}
              >
                {(props.columnItem.operator ||
                  !props.columnItem.renderType ||
                  !props.columnItem.showRenderTypeInSettings ||
                  props.field === "_count")
                    ? props.headerName
                    : `${props.headerName} - ${i18n.t(`tables.column-render-types.${props.columnItem.renderType}`)}`
                }
              </Button>
            }
          </Box>
        </Stack>
        {/* Remove Column Button */}
        <Stack direction="row" sx={{
          gap: .5
        }}>
          <Divider orientation="vertical" flexItem />
          <Tooltip title={i18n.t("tables.remove-selected-columns")}>
            <IconButton
              onClick={() => {
                props.onRemoveColumn(props.columnItem);
                props.isColumnActive && props.toggleActiveColumn();
              }}
            >
              <DeleteForever sx={{ color: theme.palette.error.main }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
}
